import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { userAPI } from "../services/api";

function Support() {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { isConnected, joinTicket, leaveTicket, sendSupportMessage, sendTyping, on, off } = useSocket();

    // States
    const [tickets, setTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [showNewTicketForm, setShowNewTicketForm] = useState(false);
    const [typingUser, setTypingUser] = useState(null);

    // Form states
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'Diger',
        message: ''
    });
    const [replyMessage, setReplyMessage] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Refs
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const selectedTicketRef = useRef(selectedTicket);

    // Keep selectedTicketRef in sync
    useEffect(() => {
        selectedTicketRef.current = selectedTicket;
    }, [selectedTicket]);

    // Scroll to bottom of messages
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Fetch tickets on mount
    useEffect(() => {
        if (user) {
            fetchTickets();
        }
    }, [user]);

    useEffect(() => {
        if (!isConnected) {
            console.log('⚠️ Socket not connected, skipping event registration');
            return;
        }

        console.log('📡 Registering socket events...');

        // Handle new messages - use ref to always get latest selectedTicket
        const handleNewMessage = (data) => {
            console.log('📩 New message received via socket:', data);
            const currentTicket = selectedTicketRef.current;

            if (currentTicket && data.ticketId === currentTicket._id) {
                console.log('📩 Message belongs to current ticket, updating...');
                setSelectedTicket(prev => ({
                    ...prev,
                    messages: [...(prev.messages || []), data.message],
                    status: data.status
                }));
                setTimeout(scrollToBottom, 100);
            }

            // Update ticket list
            setTickets(prev => prev.map(t =>
                t._id === data.ticketId
                    ? { ...t, lastActivityAt: new Date().toISOString(), status: data.status }
                    : t
            ));
        };

        // Handle typing indicator
        const handleTyping = (data) => {
            const currentTicket = selectedTicketRef.current;
            if (currentTicket && data.ticketId === currentTicket._id) {
                if (data.isTyping) {
                    setTypingUser(data.user);
                } else {
                    setTypingUser(null);
                }
            }
        };

        // Handle status changes
        const handleStatusChange = (data) => {
            const currentTicket = selectedTicketRef.current;
            if (currentTicket && data.ticketId === currentTicket._id) {
                setSelectedTicket(prev => ({ ...prev, status: data.status }));
            }
            setTickets(prev => prev.map(t =>
                t._id === data.ticketId ? { ...t, status: data.status } : t
            ));
        };

        // Handle errors
        const handleError = (error) => {
            console.error('❌ Socket error:', error);
            setError(error.message || 'Socket error occurred');
        };

        on('support:newMessage', handleNewMessage);
        on('support:typing', handleTyping);
        on('support:statusChanged', handleStatusChange);
        on('support:error', handleError);

        console.log('✅ Socket events registered');

        return () => {
            console.log('🔌 Cleaning up socket events...');
            off('support:newMessage', handleNewMessage);
            off('support:typing', handleTyping);
            off('support:statusChanged', handleStatusChange);
            off('support:error', handleError);
        };
    }, [isConnected, on, off, scrollToBottom]);

    // Join/leave ticket room when selected
    useEffect(() => {
        const ticketId = selectedTicket?._id;

        if (ticketId && isConnected) {
            joinTicket(ticketId);
            setTimeout(scrollToBottom, 100);
        }

        return () => {
            if (ticketId) {
                leaveTicket(ticketId);
            }
        };
    }, [selectedTicket?._id, isConnected, joinTicket, leaveTicket, scrollToBottom]);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const response = await userAPI.getSupportTickets();
            setTickets(response.data || []);
        } catch (err) {
            console.error('Failed to fetch tickets:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject.trim() || !newTicket.message.trim()) return;

        setSendingMessage(true);
        setError('');
        try {
            await userAPI.createSupportTicket(newTicket);
            setSuccess('Destek talebiniz olusturuldu!');
            setNewTicket({ subject: '', category: 'Diger', message: '' });
            setShowNewTicketForm(false);
            fetchTickets();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Talep olusturulamadi');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim() || !selectedTicket) return;

        const messageToSend = replyMessage.trim();
        console.log('💬 Sending reply...', { ticketId: selectedTicket._id, isConnected });

        // Use WebSocket if connected, fallback to API
        if (isConnected) {
            const sent = sendSupportMessage(selectedTicket._id, messageToSend);
            console.log('💬 Socket message sent:', sent);

            if (sent) {
                setReplyMessage('');
                sendTyping(selectedTicket._id, false);
            } else {
                // Fallback to API if socket send failed
                console.log('⚠️ Socket send failed, falling back to API');
                setSendingMessage(true);
                try {
                    await userAPI.replyToSupportTicket(selectedTicket._id, messageToSend);
                    setReplyMessage('');
                    const response = await userAPI.getSupportTicket(selectedTicket._id);
                    setSelectedTicket(response.data);
                    fetchTickets();
                } catch (err) {
                    setError(err.message || 'Mesaj gonderilemedi');
                } finally {
                    setSendingMessage(false);
                }
            }
        } else {
            console.log('📡 Socket not connected, using API');
            setSendingMessage(true);
            setError('');
            try {
                await userAPI.replyToSupportTicket(selectedTicket._id, messageToSend);
                setReplyMessage('');
                const response = await userAPI.getSupportTicket(selectedTicket._id);
                setSelectedTicket(response.data);
                fetchTickets();
            } catch (err) {
                setError(err.message || 'Mesaj gonderilemedi');
            } finally {
                setSendingMessage(false);
            }
        }
    };

    // Handle typing indicator
    const handleInputChange = (e) => {
        setReplyMessage(e.target.value);

        if (selectedTicket && isConnected) {
            sendTyping(selectedTicket._id, true);

            // Clear existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Stop typing after 2 seconds of inactivity
            typingTimeoutRef.current = setTimeout(() => {
                sendTyping(selectedTicket._id, false);
            }, 2000);
        }
    };

    const selectTicket = async (ticket) => {
        setShowNewTicketForm(false);
        try {
            const response = await userAPI.getSupportTicket(ticket._id);
            setSelectedTicket(response.data);
        } catch (err) {
            console.error('Failed to get ticket:', err);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return '#f59e0b';
            case 'in_progress': return '#3b82f6';
            case 'resolved': return '#22c55e';
            case 'closed': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'open': return 'Acik';
            case 'in_progress': return 'Islemde';
            case 'waiting_user': return 'Yanit Bekleniyor';
            case 'resolved': return 'Cozuldu';
            case 'closed': return 'Kapali';
            default: return status;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Not logged in view
    if (!user) {
        return (
            <div className="app-container">
                <Navbar />
                <main className="main-content">
                    <header className="page-header support-header">
                        <h1 className="page-title">{t('support.title')}</h1>
                    </header>
                    <section className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
                        <p style={{ marginBottom: '20px', color: 'var(--text-muted)' }}>
                            Destek talebi olusturmak icin giris yapin.
                        </p>
                        <a href="/login" className="btn btn-primary">
                            Giris Yap
                        </a>
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="app-container">
            <Navbar />

            <main className="main-content">
                <header className="page-header support-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h1 className="page-title">{t('support.title')}</h1>
                        {isConnected && (
                            <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: '#22c55e',
                                display: 'inline-block'
                            }} title="Canli baglanti aktif" />
                        )}
                    </div>
                </header>

                {/* Success/Error Messages */}
                {success && (
                    <div style={{
                        maxWidth: '900px',
                        margin: '0 auto 16px',
                        padding: '12px 16px',
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '8px',
                        color: '#22c55e'
                    }}>
                        {success}
                    </div>
                )}

                {error && (
                    <div style={{
                        maxWidth: '900px',
                        margin: '0 auto 16px',
                        padding: '12px 16px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        color: '#ef4444'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{
                    maxWidth: '900px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: selectedTicket ? '300px 1fr' : '1fr',
                    gap: '20px'
                }}>
                    {/* Ticket List Panel */}
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        {/* Header */}
                        <div style={{
                            padding: '16px',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '1rem' }}>Taleplerim</h3>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                    setShowNewTicketForm(true);
                                    setSelectedTicket(null);
                                }}
                                style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            >
                                + Yeni Talep
                            </button>
                        </div>

                        {/* Ticket List */}
                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {loading ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    Yukleniyor...
                                </div>
                            ) : tickets.length === 0 ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    <p>Henuz destek talebiniz yok</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowNewTicketForm(true)}
                                        style={{ marginTop: '12px' }}
                                    >
                                        Ilk Talebinizi Olusturun
                                    </button>
                                </div>
                            ) : (
                                tickets.map(ticket => (
                                    <div
                                        key={ticket._id}
                                        onClick={() => selectTicket(ticket)}
                                        style={{
                                            padding: '14px 16px',
                                            borderBottom: '1px solid var(--border-color)',
                                            cursor: 'pointer',
                                            background: selectedTicket?._id === ticket._id ? 'var(--bg-tertiary)' : 'transparent',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                        onMouseLeave={e => e.currentTarget.style.background = selectedTicket?._id === ticket._id ? 'var(--bg-tertiary)' : 'transparent'}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                            <span style={{
                                                fontSize: '0.7rem',
                                                color: 'var(--text-muted)',
                                                fontFamily: 'monospace'
                                            }}>
                                                #{ticket.ticketNumber}
                                            </span>
                                            <span style={{
                                                fontSize: '0.7rem',
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                background: getStatusColor(ticket.status) + '20',
                                                color: getStatusColor(ticket.status)
                                            }}>
                                                {getStatusLabel(ticket.status)}
                                            </span>
                                        </div>
                                        <div style={{
                                            fontWeight: '500',
                                            fontSize: '0.9rem',
                                            marginBottom: '4px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {ticket.subject}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            {formatDate(ticket.lastActivityAt || ticket.createdAt)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right Panel - New Ticket Form or Conversation */}
                    {(showNewTicketForm || selectedTicket) && (
                        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {showNewTicketForm ? (
                                /* New Ticket Form */
                                <>
                                    <div style={{
                                        padding: '16px',
                                        borderBottom: '1px solid var(--border-color)',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <h3 style={{ margin: 0, fontSize: '1rem' }}>Yeni Destek Talebi</h3>
                                        <button
                                            onClick={() => setShowNewTicketForm(false)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.2rem' }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <form onSubmit={handleCreateTicket} style={{ padding: '16px' }}>
                                        <div className="form-group" style={{ marginBottom: '16px' }}>
                                            <label className="form-label">Konu *</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Talebinizin konusu"
                                                value={newTicket.subject}
                                                onChange={e => setNewTicket({...newTicket, subject: e.target.value})}
                                                required
                                            />
                                        </div>
                                        <div className="form-group" style={{ marginBottom: '16px' }}>
                                            <label className="form-label">Kategori</label>
                                            <select
                                                className="form-input"
                                                value={newTicket.category}
                                                onChange={e => setNewTicket({...newTicket, category: e.target.value})}
                                            >
                                                <option value="Teknik">Teknik Sorun</option>
                                                <option value="Odeme">Odeme / Abonelik</option>
                                                <option value="Hesap">Hesap</option>
                                                <option value="Oneri">Oneri</option>
                                                <option value="Sikayet">Sikayet</option>
                                                <option value="Diger">Diger</option>
                                            </select>
                                        </div>
                                        <div className="form-group" style={{ marginBottom: '16px' }}>
                                            <label className="form-label">Mesajiniz *</label>
                                            <textarea
                                                className="form-input"
                                                placeholder="Sorununuzu veya talebinizi detayli olarak aciklayin..."
                                                rows="5"
                                                value={newTicket.message}
                                                onChange={e => setNewTicket({...newTicket, message: e.target.value})}
                                                required
                                                style={{ resize: 'vertical', minHeight: '120px' }}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            style={{ width: '100%' }}
                                            disabled={sendingMessage}
                                        >
                                            {sendingMessage ? 'Gonderiliyor...' : 'Talep Olustur'}
                                        </button>
                                    </form>
                                </>
                            ) : selectedTicket && (
                                /* Ticket Conversation */
                                <>
                                    {/* Ticket Header */}
                                    <div style={{
                                        padding: '16px',
                                        borderBottom: '1px solid var(--border-color)',
                                        background: 'var(--bg-tertiary)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                                #{selectedTicket.ticketNumber}
                                            </span>
                                            <span style={{
                                                fontSize: '0.75rem',
                                                padding: '3px 10px',
                                                borderRadius: '12px',
                                                background: getStatusColor(selectedTicket.status) + '20',
                                                color: getStatusColor(selectedTicket.status),
                                                fontWeight: '500'
                                            }}>
                                                {getStatusLabel(selectedTicket.status)}
                                            </span>
                                        </div>
                                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>{selectedTicket.subject}</h3>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                background: 'var(--bg-secondary)',
                                                borderRadius: '4px',
                                                marginRight: '8px'
                                            }}>
                                                {selectedTicket.category}
                                            </span>
                                            Olusturulma: {formatDate(selectedTicket.createdAt)}
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div style={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        padding: '16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        minHeight: '250px',
                                        maxHeight: '350px'
                                    }}>
                                        {selectedTicket.messages?.map((msg, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                                    maxWidth: '85%'
                                                }}
                                            >
                                                <div style={{
                                                    padding: '12px 16px',
                                                    borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                    background: msg.sender === 'user'
                                                        ? 'var(--accent-gradient)'
                                                        : 'var(--bg-tertiary)',
                                                    color: msg.sender === 'user' ? 'white' : 'inherit'
                                                }}>
                                                    <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                        {msg.message}
                                                    </div>
                                                </div>
                                                <div style={{
                                                    fontSize: '0.7rem',
                                                    color: 'var(--text-muted)',
                                                    marginTop: '4px',
                                                    textAlign: msg.sender === 'user' ? 'right' : 'left'
                                                }}>
                                                    {msg.sender === 'admin' && <span style={{ marginRight: '6px' }}>Destek Ekibi</span>}
                                                    {formatDate(msg.createdAt)}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Typing indicator */}
                                        {typingUser && (
                                            <div style={{
                                                alignSelf: 'flex-start',
                                                padding: '8px 16px',
                                                background: 'var(--bg-tertiary)',
                                                borderRadius: '16px',
                                                fontSize: '0.85rem',
                                                color: 'var(--text-muted)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <span>{typingUser} yaziyor</span>
                                                <span className="typing-dots">
                                                    <span>.</span><span>.</span><span>.</span>
                                                </span>
                                            </div>
                                        )}

                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Reply Form */}
                                    {selectedTicket.status !== 'closed' && (
                                        <form onSubmit={handleReply} style={{
                                            padding: '16px',
                                            borderTop: '1px solid var(--border-color)',
                                            display: 'flex',
                                            gap: '12px'
                                        }}>
                                            <input
                                                type="text"
                                                className="form-input"
                                                placeholder="Mesajinizi yazin..."
                                                value={replyMessage}
                                                onChange={handleInputChange}
                                                style={{ flex: 1 }}
                                            />
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={sendingMessage || !replyMessage.trim()}
                                            >
                                                {sendingMessage ? '...' : 'Gonder'}
                                            </button>
                                        </form>
                                    )}

                                    {selectedTicket.status === 'closed' && (
                                        <div style={{
                                            padding: '16px',
                                            borderTop: '1px solid var(--border-color)',
                                            textAlign: 'center',
                                            color: 'var(--text-muted)',
                                            fontSize: '0.85rem'
                                        }}>
                                            Bu talep kapatilmistir.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Show message if no ticket selected and not creating new */}
                    {!selectedTicket && !showNewTicketForm && tickets.length > 0 && (
                        <div className="card" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-muted)',
                            minHeight: '300px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.5 }}>💬</div>
                                <p>Detaylari gormek icin bir talep secin</p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />

            <style>{`
                .typing-dots span {
                    animation: typingDot 1.4s infinite;
                    opacity: 0;
                }
                .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
                .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes typingDot {
                    0%, 60%, 100% { opacity: 0; }
                    30% { opacity: 1; }
                }
            `}</style>
        </div>
    );
}

export default Support;
